import { Button } from "@repo/ui/button";
import { Input } from "@repo/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@repo/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@repo/ui/tooltip";
import { Filter, Plus, RefreshCw, Search, Shield } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CreateJobDialog } from "@/components/jobs/CreateJobDialog";
import { JobsTable } from "@/components/jobs/JobsTable";
import { useAuth } from "@/hooks/useAuth";

const Jobs = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const { canManageJobs, isViewer } = useAuth();
  // Modal open state is now URL-route-driven: /dashboard/jobs/:jobId opens
  // the dialog for that job. /dashboard/jobs (no jobId) shows the bare table.
  // This means browser Back from the run-detail page lands on the modal route
  // and reopens the dialog automatically.
  const { jobId } = useParams<{ jobId?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Bookmark-compat: redirect legacy ?jobId=... URLs to the new nested path
  // and replace history so the back button works correctly.
  useEffect(() => {
    const legacyJobId = searchParams.get("jobId");
    if (legacyJobId && !jobId) {
      navigate(`/dashboard/jobs/${legacyJobId}`, { replace: true });
    }
  }, [searchParams, jobId, navigate]);

  const openJobId = jobId ?? undefined;

  const handleRefresh = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Scrape Jobs</h1>
          <p className="text-muted-foreground">
            {isViewer
              ? "View data scraping operations (read-only)"
              : "Manage and monitor your data scraping operations"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={handleRefresh}>
            <RefreshCw className="w-4 h-4" />
          </Button>
          {canManageJobs ? (
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              New Job
            </Button>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button disabled variant="outline" className="gap-2">
                  <Shield className="w-4 h-4" />
                  View Only
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Contact an admin or operator to create jobs</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>

      <div className="card-elevated">
        <div className="p-4 border-b border-border">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="jobs-search"
                name="jobs-search"
                placeholder="Search jobs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button variant="outline">
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </Button>
          </div>
        </div>

        <Tabs defaultValue="all" className="w-full">
          <div className="px-4 border-b border-border">
            <TabsList className="bg-transparent h-12 p-0 gap-4">
              <TabsTrigger
                value="all"
                className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-accent rounded-none px-1"
              >
                All Jobs
              </TabsTrigger>
              <TabsTrigger
                value="running"
                className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-accent rounded-none px-1"
              >
                Running
              </TabsTrigger>
              <TabsTrigger
                value="scheduled"
                className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-accent rounded-none px-1"
              >
                Scheduled
              </TabsTrigger>
              <TabsTrigger
                value="completed"
                className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-accent rounded-none px-1"
              >
                Completed
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="all" className="m-0">
            <JobsTable
              key={`all-${refreshKey}`}
              filter="all"
              searchQuery={searchQuery}
              openJobId={openJobId}
            />
          </TabsContent>
          <TabsContent value="running" className="m-0">
            <JobsTable
              key={`running-${refreshKey}`}
              filter="running"
              searchQuery={searchQuery}
              openJobId={openJobId}
            />
          </TabsContent>
          <TabsContent value="scheduled" className="m-0">
            <JobsTable
              key={`scheduled-${refreshKey}`}
              filter="scheduled"
              searchQuery={searchQuery}
              openJobId={openJobId}
            />
          </TabsContent>
          <TabsContent value="completed" className="m-0">
            <JobsTable
              key={`completed-${refreshKey}`}
              filter="completed"
              searchQuery={searchQuery}
              openJobId={openJobId}
            />
          </TabsContent>
        </Tabs>
      </div>

      {canManageJobs && (
        <CreateJobDialog
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
          onJobCreated={handleRefresh}
        />
      )}
    </div>
  );
};

export default Jobs;
